import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react"
import { apiClient } from "@/shared/services/client"
import type { AccesoRapidoItem, AccesosRapidosState } from "@/shared/types/accesos-rapidos"
import { useAuth } from "@/auth/context/AuthContext"
import { toast } from "sonner"

interface AccesosRapidosContextType extends AccesosRapidosState {
  addAccesoRapido: (item: AccesoRapidoItem) => Promise<boolean>
  removeAccesoRapido: (id: string) => Promise<boolean>
  isAccesoRapido: (id: string) => boolean
  toggleAccesoRapido: (item: AccesoRapidoItem) => Promise<void>
}

const AccesosRapidosContext = createContext<AccesosRapidosContextType | undefined>(undefined)

export function AccesosRapidosProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  const [state, setState] = useState<AccesosRapidosState>({
    items: [],
    isLoading: true,
  })

  // Cargar accesos rapidos al autenticar
  const fetchAccesosRapidos = useCallback(async () => {
    if (!isAuthenticated) {
      setState({ items: [], isLoading: false })
      return
    }

    try {
      const response = await apiClient.get<{ items: AccesoRapidoItem[] }>("/auth/accesos-rapidos/")
      setState({ items: response.data.items || [], isLoading: false })
    } catch {
      setState({ items: [], isLoading: false })
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchAccesosRapidos()
  }, [fetchAccesosRapidos, user])

  const addAccesoRapido = useCallback(async (item: AccesoRapidoItem): Promise<boolean> => {
    try {
      const response = await apiClient.post<{
        success: boolean
        message: string
        items: AccesoRapidoItem[]
      }>("/auth/accesos-rapidos/", item)

      if (response.data.success) {
        setState(prev => ({ ...prev, items: response.data.items }))
        toast.success(response.data.message)
        return true
      } else {
        toast.error(response.data.message)
        return false
      }
    } catch {
      toast.error("Error al agregar acceso rapido")
      return false
    }
  }, [])

  const removeAccesoRapido = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await apiClient.delete<{
        success: boolean
        message: string
        items: AccesoRapidoItem[]
      }>(`/auth/accesos-rapidos/${id}/`)

      if (response.data.success) {
        setState(prev => ({ ...prev, items: response.data.items }))
        toast.success(response.data.message)
        return true
      } else {
        toast.error(response.data.message)
        return false
      }
    } catch {
      toast.error("Error al eliminar acceso rapido")
      return false
    }
  }, [])

  const isAccesoRapido = useCallback((id: string): boolean => {
    return state.items.some(item => item.id === id)
  }, [state.items])

  const toggleAccesoRapido = useCallback(async (item: AccesoRapidoItem): Promise<void> => {
    if (isAccesoRapido(item.id)) {
      await removeAccesoRapido(item.id)
    } else {
      await addAccesoRapido(item)
    }
  }, [isAccesoRapido, removeAccesoRapido, addAccesoRapido])

  return (
    <AccesosRapidosContext.Provider
      value={{
        ...state,
        addAccesoRapido,
        removeAccesoRapido,
        isAccesoRapido,
        toggleAccesoRapido,
      }}
    >
      {children}
    </AccesosRapidosContext.Provider>
  )
}

export function useAccesosRapidos() {
  const context = useContext(AccesosRapidosContext)
  if (context === undefined) {
    throw new Error("useAccesosRapidos debe ser usado dentro de AccesosRapidosProvider")
  }
  return context
}
